import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from '@mui/material';
import { useWatch } from 'react-hook-form';
import FormSchemaParser from '@/components/Common/FormSchemaParser';

interface TebraFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: (val: any) => void;
}

const IntakeFormsField = ({
  field,
  namePrefix,
  control,
  getValues,
  setValue,
  disabled
}: {
  field: any;
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
}) => {
  const fieldName = `${namePrefix}.${field.key}`;
  const groups = field.groups || [];
  const selectedForms = useWatch({ control, name: fieldName }) || [];
  const selectedFormIds = selectedForms.map((value: any) => String(value));

  const updateSelectedForms = (formIds: any[]) => {
    setValue(fieldName, formIds, { shouldDirty: true });
  };

  const isFormSelected = (formId: any) => selectedFormIds.includes(String(formId));

  const toggleGroup = (group: any, checked: boolean) => {
    const groupFormIds = group.forms.map((form: any) => form.value);
    let current = [...(getValues(fieldName) || [])];

    if (checked) {
      groupFormIds.forEach((formId: any) => {
        if (!current.map((value: any) => String(value)).includes(String(formId))) {
          current.push(formId);
        }
      });
    } else {
      const groupFormIdStrings = groupFormIds.map((formId: any) => String(formId));
      current = current.filter((value: any) => !groupFormIdStrings.includes(String(value)));
    }

    updateSelectedForms(current);
  };

  const isGroupChecked = (group: any) => group.forms.every((form: any) => isFormSelected(form.value));

  const isGroupIndeterminate = (group: any) => {
    const selectedCount = group.forms.filter((form: any) => isFormSelected(form.value)).length;
    return selectedCount > 0 && selectedCount < group.forms.length;
  };

  const getSelectedLabels = () => {
    const labels: string[] = [];
    groups.forEach((group: any) => {
      group.forms.forEach((form: any) => {
        if (isFormSelected(form.value)) labels.push(form.label);
      });
    });
    return labels.join(', ');
  };

  return (
    <Box my={3}>
      <FormControl fullWidth disabled={disabled}>
        <InputLabel>{field.label || 'Intake Forms'}</InputLabel>
        <Select
          multiple
          value={selectedForms}
          onChange={(event) => updateSelectedForms(event.target.value as any[])}
          input={<OutlinedInput label={field.label || 'Intake Forms'} />}
          renderValue={() => getSelectedLabels()}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              style: { maxHeight: 360 }
            }
          }}
        >
          {groups.map((group: any) => [
            <ListSubheader
              key={`header-${group.key}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary', lineHeight: 'unset', py: 1 }}
              onClick={(event) => event.stopPropagation()}
            >
              <Checkbox
                checked={isGroupChecked(group)}
                indeterminate={isGroupIndeterminate(group)}
                onChange={(event) => toggleGroup(group, event.target.checked)}
                disabled={disabled}
                size='small'
              />
              <Typography fontWeight={600} fontSize={14}>
                {group.label}
              </Typography>
            </ListSubheader>,
            ...group.forms.map((form: any) => (
              <MenuItem key={String(form.value)} value={form.value} sx={{ pl: 4 }}>
                <Checkbox checked={isFormSelected(form.value)} size='small' sx={{ mr: 1 }} />
                {form.label}
              </MenuItem>
            ))
          ])}
        </Select>
      </FormControl>
    </Box>
  );
};

const TebraForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: TebraFormProps) => {
  const intakeFormsField = configFields?.find((field: any) => field.key === 'intake_forms');
  const providersField = configFields?.find((field: any) => field.key === 'providers');
  const otherFields = configFields?.filter((field: any) => !['intake_forms', 'providers'].includes(field.key));

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      {intakeFormsField && (
        <IntakeFormsField
          field={intakeFormsField}
          namePrefix={namePrefix}
          control={control}
          getValues={getValues}
          setValue={setValue}
          disabled={disabled}
        />
      )}

      {providersField && (
        <FormSchemaParser
          disabled={disabled}
          fields={[providersField]}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}
    </>
  );
};

export default TebraForm;
